package dev.lingvo.shared.screenshots.scene

import dev.lingvo.shared.screenshots.doc.Anchor
import dev.lingvo.shared.screenshots.doc.Box
import dev.lingvo.shared.screenshots.doc.CanvasSize
import dev.lingvo.shared.screenshots.doc.CaptionAt
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.Role
import dev.lingvo.shared.screenshots.doc.SceneKind
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.max
import kotlin.math.min
import kotlin.math.round
import kotlin.math.sin

/** Pure arrangements let template selection and editor resizing share the same geometric rules. */
object Scenes : SceneArranger {
    /** Sizes devices from capture aspect instead of stretching their screens to fill arbitrary boxes. */
    override fun arrange(input: SceneInput): List<Placement> {
        require(input.slots in 1..10 && input.canvas.width > 0 && input.canvas.height > 0)
        require(input.screenAspect.isFinite() && input.screenAspect > 0)
        require(listOf(input.safe.x, input.safe.y, input.safe.w, input.safe.h).all { it.isFinite() } && input.safe.w > 0 && input.safe.h > 0)
        val w = input.canvas.width.toDouble()
        val h = input.canvas.height.toDouble()
        val m = round(.06 * w)
        val landscape = w > h
        val regionX = if (landscape) .42 * w else 0.0
        val regionY = if (landscape) m else 0.0
        val rw = if (landscape) .58 * w - m else w
        val rh = if (landscape) h - 2 * m else h
        val placements = mutableListOf<Placement>()
        val count = if (input.spec.kind == SceneKind.PANORAMA) input.slots else 1
        fun caption(k: Int): Box {
            val over = input.spec.kind == SceneKind.CAPTION_OVER_DEVICE
            val raw = when {
                landscape -> Box(m, m, .36 * w, h - 2 * m)
                over -> Box(m, .70 * h, w - 2 * m, .20 * h)
                input.spec.captionAt == CaptionAt.BOTTOM -> Box(m, h - .075 * h - input.spec.captionShare * h, w - 2 * m, input.spec.captionShare * h)
                else -> Box(m, .075 * h, w - 2 * m, input.spec.captionShare * h)
            }
            val width = min(raw.w, input.safe.w)
            val height = min(raw.h, input.safe.h)
            val x = raw.x.coerceIn(input.safe.x, max(input.safe.x, input.safe.right - width))
            val y = raw.y.coerceIn(input.safe.y, max(input.safe.y, input.safe.bottom - height))
            return raw.copy(x = x + k * w, y = y, w = width, h = height)
        }
        fun device(role: Role, slot: Int, width: Double, cx: Double, top: Double, rotation: Double = 0.0, z: Int = 2, bottomPivot: Boolean = false) {
            val height = width * frameOuterAspect(input.frame, input.screenAspect)
            val radians = rotation * PI / 180
            val dx = if (bottomPivot) sin(radians) * height / 2 else 0.0
            val dy = if (bottomPivot) (1 - cos(radians)) * height / 2 else 0.0
            placements += Placement(role, slot, Box(regionX + cx - width / 2 + dx + slot * w, regionY + top + dy, width, height, rotation), z)
        }
        repeat(count) { k -> placements += Placement(Role.CAPTION, k, caption(k), 10) }
        val tilt = input.spec.tilt
        when (input.spec.kind) {
            SceneKind.SINGLE -> {
                val width = .80 * rw * input.spec.deviceScale
                val cx = when (input.spec.deviceAnchor) {
                    Anchor.START -> (if (landscape) 0.0 else m) + width / 2
                    Anchor.CENTER -> rw / 2
                    Anchor.END -> rw - (if (landscape) 0.0 else m) - width / 2
                }
                val captionBox = placements.first { it.role == Role.CAPTION }.box
                val topBlock = captionBox.y + captionBox.h + .03 * rh
                val top = when {
                    landscape -> 0.0
                    input.spec.captionAt == CaptionAt.BOTTOM -> captionBox.y - .03 * rh - width * frameOuterAspect(input.frame, input.screenAspect)
                    else -> topBlock
                }
                device(Role.DEVICE_1, 0, width, cx, top)
            }
            SceneKind.CAPTION_OVER_DEVICE -> {
                device(Role.DEVICE_1, 0, .90 * rw, rw / 2, .06 * rh)
                placements += Placement(Role.SCRIM, 0, Box(0.0, .55 * h, w, .45 * h), 8)
            }
            SceneKind.TILTED_PAIR -> {
                device(Role.DEVICE_1, 0, .62 * rw, .60 * rw, .33 * rh, -tilt, 2)
                device(Role.DEVICE_2, 0, .62 * .92 * rw, .36 * rw, .27 * rh, -tilt, 1)
            }
            SceneKind.FAN_OF_THREE -> {
                device(Role.DEVICE_2, 0, .50 * rw, .50 * rw, .34 * rh, z = 3)
                device(Role.DEVICE_1, 0, .45 * rw, .23 * rw, .38 * rh, -1.5 * tilt, 1, true)
                device(Role.DEVICE_3, 0, .45 * rw, .77 * rw, .38 * rh, 1.5 * tilt, 1, true)
            }
            SceneKind.FLOATING_CARDS -> {
                device(Role.DEVICE_1, 0, .70 * rw, .44 * rw, .31 * rh)
                placements += Placement(Role.CARD_1, 0, Box(regionX + .50 * rw, regionY + .47 * rh, .44 * rw, .12 * rh), 4)
                placements += Placement(Role.CARD_2, 0, Box(regionX + .06 * rw, regionY + .66 * rh, .40 * rw, .10 * rh), 5)
            }
            SceneKind.PANORAMA -> {
                repeat(count) { k -> device(Role.DEVICE_1, k, .72 * rw, .50 * rw, .33 * rh, (if (k % 2 == 0) 1 else -1) * .75 * tilt) }
                placements += Placement(Role.RIBBON, 0, Box(0.0, .43 * h, count * w, .24 * h), 0)
            }
        }
        return placements.sortedBy { it.z }
    }

    /** Retains unused roles and their boxes so editing a neighbouring scene never destroys user content. */
    override fun apply(page: Page, input: SceneInput): Page = applyScene(page, input, arrange(input))

    /** Keeps the joined document editable using the ordinary strip-coordinate layer model. */
    override fun toPanorama(pages: List<Page>, canvas: CanvasSize): Page = joinPanorama(pages, canvas)

    /** Assigns layers by centre; arbitrary source page metadata is not represented by the v1 strip contract. */
    override fun splitPanorama(page: Page, canvas: CanvasSize): List<Page> = separatePanorama(page, canvas)
}
